import { avatarMonogramUrl } from '@/lib/identityAvatar';
/**
 * Message pop-ups: a top-right card ("кто пишет" + avatar + preview) for a DM or a
 * group-DM message, and "вас упомянули" for a mention or a reply to me in a server
 * channel. Click → opens that chat. Several messages from the SAME chat coalesce
 * into one card with a count badge instead of stacking.
 *
 * One pop-up per message, on one surface (lib/dmToast.ts messageCardSurface): the
 * in-app card when Bunker is on screen, the Desktop card when it is hidden in
 * Desktop, the platform shell when Bunker is its Chat frame and the shell does not
 * show it. «Не беспокоить» silences all of them, and so does the Cispracs ID
 * account when it turns pop-ups off for the topic.
 *
 * Built on HeroUI's Toast queue (own queue, separate from the generic app toast
 * provider in App.tsx) so it gets stacking (maxVisibleToasts), pause-on-hover
 * and placement for free.
 */
import { Toast, ToastQueue } from '@heroui/react';
import type { ChannelActivityPayload, DmActivityPayload } from '@bunker/shared/socketEvents';
import { passesMuteLevel } from '@bunker/shared/mutes';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { getChannelLevel, type NotificationLevel } from '@/lib/muteSettings';
import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import i18n from '@/i18n';
import { truncatePreview, messageCardSurface, nextDmToastCount } from '@/lib/dmToast';
import { isBunkerOnScreen, canShowShellNotification, showShellNotification } from '@/lib/desktopBridge';
import { isDoNotDisturb } from '@/lib/notifications';
import { isPlatformEmbedded } from '@/lib/platformEmbed';
import { notifyShell } from '@/lib/shellNotifications';
import { buildMessageLink } from '@/lib/messageLink';
import { accountAllows, type AccountNotifyTopic } from '@bunker/shared/notifyPrefs';
import { currentAccountNotifyPrefs } from '@/lib/accountNotifyPrefs';

/** The content payload carried by each queued card. Everything is resolved
 *  up-front so the card render stays a pure presentation. */
interface DmToastView {
  channelId: string;
  /** The server of a channel card; null for a DM. */
  guildId: string | null;
  senderName: string;
  avatarUrl: string | null;
  authorId: string;
  /** The group DM's name, or `#channel · server`; null for a one-to-one DM. */
  groupName: string | null;
  preview: string;
  /** How many messages from this chat are folded into this card (≥1). */
  count: number;
}

/** Dedicated queue — max 3 visible cards at once (the rest wait their turn). */
export const dmToastQueue = new ToastQueue<DmToastView>({ maxVisibleToasts: 3 });

const DISMISS_MS = 5000;

/** Tracks the live card per chat so a follow-up message coalesces (close + re-add
 *  with an incremented count) instead of opening a second card. */
const openByChannel = new Map<string, { key: string; count: number }>();

type CardKind = 'dm' | 'mention' | 'reply';

function surfaceFor(input: {
  authorId: string;
  myId: string;
  channelId: string;
  level: NotificationLevel;
  topic: AccountNotifyTopic;
}) {
  return messageCardSurface({
    isOwnMessage: input.authorId === input.myId,
    level: input.level,
    doNotDisturb: isDoNotDisturb(),
    accountPopup: accountAllows(currentAccountNotifyPrefs(), input.topic, 'popup'),
    bunkerOnScreen: isBunkerOnScreen(),
    viewingThisChannel: useDataStore.getState().currentChannelId === input.channelId,
    desktopCard: canShowShellNotification(),
    platformFrame: isPlatformEmbedded(),
  });
}

/** Puts one resolved card on the surface messageCardSurface picked. */
function deliver(
  surface: ReturnType<typeof messageCardSurface>,
  view: Omit<DmToastView, 'count'>,
  meta: { kind: CardKind; messageId: string },
): void {
  if (surface === 'none') return;
  if (surface === 'desktop') {
    showShellNotification({
      channelId: view.channelId,
      senderName: view.senderName,
      avatarUrl: view.avatarUrl ?? avatarMonogramUrl(view.senderName, view.authorId),
      groupName: view.groupName,
      preview: view.preview,
    });
    return;
  }
  if (surface === 'shell') {
    notifyShell({
      id: meta.messageId,
      kind: meta.kind,
      title: view.senderName,
      context: view.groupName,
      body: view.preview,
      avatarUrl: view.avatarUrl && /^https:\/\//.test(view.avatarUrl) ? view.avatarUrl : null,
      channelId: view.channelId,
      guildId: view.guildId,
      path: buildMessageLink({ guildId: view.guildId, channelId: view.channelId, messageId: meta.messageId }, ''),
    });
    return;
  }

  const prev = openByChannel.get(view.channelId);
  const count = nextDmToastCount(prev?.count);
  if (prev) dmToastQueue.close(prev.key);

  const key = dmToastQueue.add(
    { ...view, count },
    {
      timeout: DISMISS_MS,
      onClose: () => {
        // Only clear the map if THIS card is still the live one for the chat —
        // a coalesced re-add already replaced the entry with a newer key.
        if (openByChannel.get(view.channelId)?.key === key) openByChannel.delete(view.channelId);
      },
    },
  );
  openByChannel.set(view.channelId, { key, count });
}

/**
 * Pop up an incoming `dm:activity`. Self-gating: returns early when no card should
 * show (own message, muted, «Не беспокоить», already reading that chat). Safe to
 * call for every dm:activity.
 */
export function showDmToast(p: DmActivityPayload): void {
  const myId = useAuthStore.getState().user?.id;
  if (!myId) return;

  const state = useDataStore.getState();
  const level = getChannelLevel(p.channelId, null); // DMs have no guild fallback
  const surface = surfaceFor({ authorId: p.authorId, myId, channelId: p.channelId, level, topic: 'dm' });
  if (surface === 'none') return;

  // We need the participant list to resolve the sender's avatar + the chat name.
  // A not-yet-hydrated / partial channel entry has no participants — skip the
  // card (the unread bump already happened separately); we just don't toast.
  const dm = state.dmChannels[p.channelId];
  if (!dm || !Array.isArray(dm.directParticipants)) return;

  const sender = dm.directParticipants.find((pp) => pp.userId === p.authorId);
  const senderName =
    p.authorName ||
    sender?.user.displayName ||
    sender?.user.username ||
    i18n.t('lib2.notify.directMessages');
  const groupName =
    dm.type === 'GROUP_DM'
      ? dm.name ||
        dm.directParticipants
          .filter((pp) => pp.userId !== myId)
          .slice(0, 3)
          .map((pp) => pp.user.displayName || pp.user.username)
          .join(', ') ||
        i18n.t('lib2.notify.group')
      : null;
  const preview = p.preview ? truncatePreview(p.preview) : i18n.t('lib2.notify.dmAttachment');

  deliver(
    surface,
    { channelId: p.channelId, guildId: null, authorId: p.authorId, senderName, avatarUrl: sender?.user.avatarUrl ?? null, groupName, preview },
    { kind: 'dm', messageId: p.messageId },
  );
}

/**
 * Pop up a mention of me or a reply to me in a server channel (`channel:activity`).
 * The event goes to the whole server room and carries no text, so the card says
 * who and where, not what. Self-gating like showDmToast.
 */
export function showMentionToast(p: ChannelActivityPayload): void {
  const myId = useAuthStore.getState().user?.id;
  if (!myId) return;
  const mentioned = p.mentions.includes(myId);
  const repliedTo = p.replyToAuthorId === myId;
  if (!mentioned && !repliedTo) return;

  const level = getChannelLevel(p.channelId, p.guildId);
  if (!passesMuteLevel(level, { isDm: false, mentioned, repliedTo })) return;
  const surface = surfaceFor({ authorId: p.authorId, myId, channelId: p.channelId, level, topic: 'mention' });
  if (surface === 'none') return;

  const guild = useDataStore.getState().guilds[p.guildId];
  const channel = guild?.channels.find((c) => c.id === p.channelId);
  if (!guild || !channel) return;

  deliver(
    surface,
    {
      channelId: p.channelId,
      guildId: p.guildId,
      authorId: p.authorId,
      senderName: p.authorName || i18n.t('lib2.notify.newInChannel'),
      avatarUrl: null,
      groupName: `#${channel.name ?? i18n.t('lib2.notify.channel')} · ${guild.name}`,
      preview: i18n.t(mentioned ? 'lib2.notify.mentioned' : 'lib2.notify.reply'),
    },
    { kind: mentioned ? 'mention' : 'reply', messageId: p.messageId },
  );
}

/** Mounts the card region (top-right). Rendered once near the app root. */
export function DmToastProvider() {
  return (
    <Toast.Provider placement="top end" queue={dmToastQueue} width={360}>
      {({ toast: item }) => {
        const c = item.content;
        const open = () => {
          const store = useDataStore.getState();
          if (c.guildId) {
            store.selectGuild(c.guildId);
            store.selectChannel(c.channelId);
          } else {
            store.selectDirectChannel(c.channelId);
          }
          dmToastQueue.close(item.key);
        };
        return (
          <Toast
            toast={item}
            className="rounded-2xl border border-separator bg-surface shadow-lg"
          >
            <button
              type="button"
              onClick={open}
              aria-label={`${c.senderName}: ${c.preview}`}
              className="flex w-full items-center gap-3 pr-6 text-left"
            >
              <IdentityAvatar id={c.authorId} name={c.senderName} src={c.avatarUrl} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-foreground">
                    {c.senderName}
                  </span>
                  {c.groupName && (
                    <span className="truncate text-xs text-muted">· {c.groupName}</span>
                  )}
                </span>
                <span className="mt-0.5 line-clamp-2 text-sm text-muted">{c.preview}</span>
              </span>
              {c.count > 1 && (
                <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-white">
                  {c.count}
                </span>
              )}
            </button>
            <Toast.CloseButton className="absolute top-2 right-2 border-none bg-transparent opacity-100 [&>svg]:size-4" />
          </Toast>
        );
      }}
    </Toast.Provider>
  );
}
